"use client";
import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Undo2,
  Redo2,
  Save,
  Upload,
  Eye,
  Sidebar,
  Sun,
  Monitor,
  Tablet,
  Smartphone,
  Keyboard,
} from "lucide-react";

interface KeyboardShortcut {
  keys: string[];
  description: string;
  icon: React.ReactNode;
  category: string;
}

interface KeyboardShortcutsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

export function KeyboardShortcutsPanel({
  isOpen,
  onClose,
}: KeyboardShortcutsPanelProps) {
  const isMac =
    typeof navigator !== "undefined" && navigator.platform.includes("Mac");
  const cmdKey = isMac ? "⌘" : "Ctrl";

  const shortcuts: KeyboardShortcut[] = [
    // Editing
    {
      keys: [`${cmdKey}`, "Z"],
      description: "Undo last action",
      icon: <Undo2 className="w-4 h-4" />,
      category: "Editing",
    },
    {
      keys: isMac ? [`${cmdKey}`, "⇧", "Z"] : [`${cmdKey}`, "Y"],
      description: "Redo last action",
      icon: <Redo2 className="w-4 h-4" />,
      category: "Editing",
    },

    // Actions
    {
      keys: [`${cmdKey}`, "Alt", "S"],
      description: "Save as draft",
      icon: <Save className="w-4 h-4" />,
      category: "Actions",
    },
    {
      keys: [`${cmdKey}`, "Alt", "P"],
      description: "Publish page",
      icon: <Upload className="w-4 h-4" />,
      category: "Actions",
    },
    {
      keys: [`${cmdKey}`, "Alt", "E"],
      description: "Preview page",
      icon: <Eye className="w-4 h-4" />,
      category: "Actions",
    },

    // Interface
    {
      keys: [`${cmdKey}`, "Alt", "B"],
      description: "Toggle sidebar",
      icon: <Sidebar className="w-4 h-4" />,
      category: "Interface",
    },
    {
      keys: [`${cmdKey}`, "Alt", "T"],
      description: "Toggle light/dark theme",
      icon: <Sun className="w-4 h-4" />,
      category: "Interface",
    },

    // Device Views
    {
      keys: ["Alt", "1"],
      description: "Switch to desktop view",
      icon: <Monitor className="w-4 h-4" />,
      category: "Device Views",
    },
    {
      keys: ["Alt", "2"],
      description: "Switch to tablet view",
      icon: <Tablet className="w-4 h-4" />,
      category: "Device Views",
    },
    {
      keys: ["Alt", "3"],
      description: "Switch to mobile view",
      icon: <Smartphone className="w-4 h-4" />,
      category: "Device Views",
    },

    // Help
    {
      keys: ["⇧", "?"],
      description: "Show keyboard shortcuts",
      icon: <Keyboard className="w-4 h-4" />,
      category: "Help",
    },
  ];

  const categories = [...new Set(shortcuts.map((s) => s.category))];

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Keyboard className="w-5 h-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Speed up your workflow with these keyboard shortcuts
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                {category}
              </h3>
              <div className="space-y-2">
                {shortcuts
                  .filter((shortcut) => shortcut.category === category)
                  .map((shortcut, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-muted-foreground">
                          {shortcut.icon}
                        </div>
                        <span className="text-sm">{shortcut.description}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {shortcut.keys.map((key, keyIndex) => (
                          <Badge
                            key={keyIndex}
                            variant="outline"
                            className="px-2 py-1 text-xs font-mono font-medium"
                          >
                            {key}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Tip:</strong> Keyboard shortcuts work when you&apos;re not
            typing in input fields or text areas.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
